/**
 * @fileoverview Diálogo "Medição" do fluxo: liga o rastreio de links (e de
 * quais sites) e escolhe de onde vêm as conversões (hoje, uma loja WooCommerce
 * por webhook), com a janela de atribuição.
 *
 * O segredo do webhook aparece uma única vez, ao ligar a loja ou ao gerar um
 * segredo novo: o servidor não o devolve de novo.
 */

import React, { useEffect, useRef, useState } from "react";
import { Dialog, MenuItem, Switch } from "@material-ui/core";
import { Copy, X } from "lucide-react";
import { toast } from "react-toastify";
import ModernSelect from "../../../components/ModernSelect";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { copyToClipboard } from "../../../utils/copyToClipboard";
import { formatRelativeTime } from "../../../utils/formatRelativeTime";
import { i18n } from "../../../translate/i18n";

const t = (key, options) => i18n.t(`flowResults.measure.${key}`, options);
const WINDOW_OPTIONS = [1, 3, 7, 14, 30, 60, 90];

const CopyBox = ({ classes, label, value }) => (
  <div style={{ marginTop: 10 }}>
    <div className={classes.formLabel}>{label}</div>
    <div className={classes.codeBox}>
      <span className={classes.codeText}>{value}</span>
      <button
        type="button"
        className={`${classes.secondaryBtn} ${classes.smallBtn}`}
        onClick={async () => {
          if (await copyToClipboard(value)) toast.success(t("copied"));
        }}
      >
        <Copy size={13} /> {t("copy")}
      </button>
    </div>
  </div>
);

const MeasurementDialog = ({ classes, open, onClose, workflowId, data, onSaved }) => {
  const canTrackLinks = data.workflow.kinds.includes("ai_agent");
  const [trackLinks, setTrackLinks] = useState(false);
  const [hosts, setHosts] = useState([]);
  const [hostDraft, setHostDraft] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [windowDays, setWindowDays] = useState(7);
  const [requireClick, setRequireClick] = useState(false);
  const [sources, setSources] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [integrationId, setIntegrationId] = useState("");
  const [secret, setSecret] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [rotating, setRotating] = useState(false);

  // O formulário é preenchido só ao ABRIR. A página atualiza os indicadores
  // sozinha a cada minuto (um `data` novo): reagir a isso apagaria o que a
  // pessoa está digitando e o segredo do webhook, que só aparece uma vez.
  const dataRef = useRef(data);
  dataRef.current = data;
  useEffect(() => {
    if (!open) return;
    const { settings } = dataRef.current;
    setConfirmRotate(false);
    setTrackLinks(settings.trackLinks);
    setHosts(settings.trackLinkHosts || []);
    setHostDraft("");
    setSourceId(settings.conversionSourceId || "");
    setWindowDays(settings.conversionWindowDays);
    setRequireClick(settings.conversionRequireClick);
    setSecret(null);
    setIntegrationId("");
    api
      .get("/workflow-conversion-sources")
      .then((res) => setSources(res.data.sources || []))
      .catch(toastError);
    api
      .get("/integrations?type=woocommerce")
      .then((res) => setIntegrations(Array.isArray(res.data) ? res.data : res.data?.integrations || []))
      .catch(() => setIntegrations([]));
  }, [open]);

  const addHost = () => {
    const value = hostDraft.trim();
    if (!value) return;
    if (!hosts.includes(value)) setHosts([...hosts, value]);
    setHostDraft("");
  };

  const selectedSource = sources.find((source) => source.id === sourceId) || null;
  // Lojas cadastradas em Integrações que ainda não têm endereço de webhook.
  const freeIntegrations = integrations.filter((integration) => !sources.some((source) => source.integrationId === integration.id));

  const createSource = async () => {
    setCreating(true);
    try {
      const { data: created } = await api.post("/workflow-conversion-sources", { type: "woocommerce", integrationId });
      setSources((previous) => [...previous, created]);
      setSourceId(created.id);
      setSecret({ sourceId: created.id, value: created.secret });
      setIntegrationId("");
    } catch (err) {
      toastError(err);
    } finally {
      setCreating(false);
    }
  };

  // Trocar o segredo faz a loja parar de ser aceita até o novo ser colado lá:
  // só depois de confirmar, e uma troca por vez.
  const rotateSecret = async () => {
    if (rotating) return;
    setRotating(true);
    try {
      const { data: rotated } = await api.post(`/workflow-conversion-sources/${selectedSource.id}/rotate-secret`);
      setSecret({ sourceId: rotated.id, value: rotated.secret });
      setConfirmRotate(false);
    } catch (err) {
      toastError(err);
    } finally {
      setRotating(false);
    }
  };

  const save = async () => {
    // Site digitado e não adicionado entra junto: quem digitou quer rastreá-lo.
    // Só com o rastreio ligado: desligado, o campo nem está na tela.
    const tracking = canTrackLinks && trackLinks;
    const pending = tracking ? hostDraft.trim() : "";
    const trackLinkHosts = pending && !hosts.includes(pending) ? [...hosts, pending] : hosts;
    setSaving(true);
    try {
      await api.put(`/workflows/${workflowId}/settings`, {
        trackLinks: tracking,
        trackLinkHosts,
        conversionSourceId: sourceId || null,
        conversionWindowDays: windowDays,
        conversionRequireClick: requireClick,
      });
      toast.success(i18n.t("flowResults.saved"));
      onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      // Com o segredo na tela (só aparece uma vez), clicar fora ou apertar Esc
      // não fecha: só os botões do diálogo.
      onClose={(event, reason) => {
        if (saving || (secret && (reason === "backdropClick" || reason === "escapeKeyDown"))) return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <div className={classes.dialogTitle} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {t("title")}
        <button type="button" className={classes.chipRemove} onClick={onClose} aria-label={i18n.t("flowResults.close")}>
          <X size={18} />
        </button>
      </div>

      <div className={classes.dialogBody}>
        <div className={classes.formGroup}>
          <div className={classes.formTitle}>{t("links.title")}</div>
          {canTrackLinks ? (
            <>
              <div className={classes.formHint}>{t("links.description")}</div>
              <div className={classes.switchRow}>
                <span className={classes.formLabel}>{t("links.toggle")}</span>
                <Switch color="primary" checked={trackLinks} onChange={(e) => setTrackLinks(e.target.checked)} inputProps={{ "aria-label": t("links.toggle") }} />
              </div>
              {trackLinks && (
                <>
                  <div className={classes.formLabel} style={{ marginTop: 6 }}>
                    {t("links.hosts")}
                  </div>
                  <div className={classes.formHint}>{t("links.hostsHint")}</div>
                  <div className={classes.formRow}>
                    <input
                      className={classes.textField}
                      placeholder={t("links.hostPlaceholder")}
                      value={hostDraft}
                      onChange={(e) => setHostDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addHost();
                        }
                      }}
                      aria-label={t("links.hosts")}
                    />
                    <button type="button" className={`${classes.secondaryBtn} ${classes.smallBtn}`} onClick={addHost} disabled={!hostDraft.trim()}>
                      {t("links.addHost")}
                    </button>
                  </div>
                  {hosts.length > 0 && (
                    <div className={classes.hostChips}>
                      {hosts.map((host) => (
                        <span key={host} className={classes.hostChip}>
                          {host}
                          <button
                            type="button"
                            className={classes.chipRemove}
                            onClick={() => setHosts(hosts.filter((item) => item !== host))}
                            aria-label={t("links.removeHost", { host })}
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className={classes.formHint}>{t("links.note")}</div>
                </>
              )}
            </>
          ) : (
            <div className={classes.formHint}>{t("links.unavailable")}</div>
          )}
        </div>

        <div className={classes.formGroup}>
          <div className={classes.formTitle}>{t("conversions.title")}</div>
          <div className={classes.formHint}>{t("conversions.description")}</div>

          <div className={classes.formRow}>
            <ModernSelect
              size="small"
              fullWidth
              displayEmpty
              className={classes.dialogSelect}
              label={t("conversions.source")}
              InputLabelProps={{ shrink: true }}
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
            >
              <MenuItem value="">{t("conversions.none")}</MenuItem>
              {sources.map((source) => (
                <MenuItem key={source.id} value={source.id}>
                  {source.name}
                </MenuItem>
              ))}
            </ModernSelect>
          </div>

          {selectedSource && (
            <>
              <CopyBox classes={classes} label={t("conversions.webhookUrl")} value={selectedSource.webhookUrl} />
              {secret?.sourceId === selectedSource.id ? (
                <>
                  <CopyBox classes={classes} label={t("conversions.secret")} value={secret.value} />
                  <div className={classes.formHint}>{t("conversions.secretOnce")}</div>
                </>
              ) : confirmRotate ? (
                <div className={`${classes.banner} ${classes.bannerNeutral}`} style={{ margin: "10px 0 0", flexWrap: "wrap" }} role="alert">
                  <span style={{ flex: "1 1 220px" }}>{t("conversions.rotateWarning")}</span>
                  <button type="button" className={`${classes.secondaryBtn} ${classes.smallBtn}`} onClick={() => setConfirmRotate(false)} disabled={rotating}>
                    {i18n.t("flowResults.cancel")}
                  </button>
                  <button type="button" className={`${classes.primaryBtn} ${classes.smallBtn}`} onClick={rotateSecret} disabled={rotating}>
                    {t("conversions.rotateConfirm")}
                  </button>
                </div>
              ) : (
                <div className={classes.formHint}>
                  {t("conversions.secretHidden")}
                  <button type="button" className={classes.textBtn} onClick={() => setConfirmRotate(true)}>
                    {t("conversions.rotate")}
                  </button>
                </div>
              )}
              <div className={classes.formHint}>{t("conversions.howTo")}</div>
              <div className={classes.formHint}>
                {selectedSource.lastError
                  ? t("conversions.sourceError", { error: selectedSource.lastError })
                  : selectedSource.lastEventAt
                  ? t("conversions.lastEvent", { when: formatRelativeTime(selectedSource.lastEventAt) })
                  : t("conversions.noEvents")}
              </div>

              <div className={classes.formRow}>
                <ModernSelect
                  size="small"
                  fullWidth
                  className={classes.dialogSelect}
                  label={t("conversions.window")}
                  helperText={t("conversions.windowHint")}
                  value={windowDays}
                  onChange={(e) => setWindowDays(e.target.value)}
                >
                  {[...new Set([...WINDOW_OPTIONS, windowDays])]
                    .sort((a, b) => a - b)
                    .map((days) => (
                      <MenuItem key={days} value={days}>
                        {t("conversions.days", { count: days })}
                      </MenuItem>
                    ))}
                </ModernSelect>
              </div>
              <div className={classes.switchRow}>
                <span className={classes.formLabel}>{t("conversions.requireClick")}</span>
                <Switch
                  color="primary"
                  checked={requireClick}
                  onChange={(e) => setRequireClick(e.target.checked)}
                  inputProps={{ "aria-label": t("conversions.requireClick") }}
                />
              </div>
            </>
          )}

          {freeIntegrations.length > 0 && (
            <>
              <div className={classes.formLabel} style={{ marginTop: 16 }}>
                {t(sources.length ? "conversions.newSource" : "conversions.firstSource")}
              </div>
              <div className={classes.formRow}>
                <ModernSelect size="small" displayEmpty className={classes.filterSelect} value={integrationId} onChange={(e) => setIntegrationId(e.target.value)}>
                  <MenuItem value="">{t("conversions.chooseIntegration")}</MenuItem>
                  {freeIntegrations.map((integration) => (
                    <MenuItem key={integration.id} value={integration.id}>
                      {integration.name}
                    </MenuItem>
                  ))}
                </ModernSelect>
                <button type="button" className={`${classes.secondaryBtn} ${classes.smallBtn}`} onClick={createSource} disabled={!integrationId || creating}>
                  {t("conversions.create")}
                </button>
              </div>
            </>
          )}
          {sources.length === 0 && integrations.length === 0 && <div className={classes.formHint}>{t("conversions.noIntegrations")}</div>}
        </div>
      </div>

      <div className={classes.dialogActions}>
        <button type="button" className={classes.secondaryBtn} onClick={onClose} disabled={saving}>
          {i18n.t("flowResults.cancel")}
        </button>
        <button type="button" className={classes.primaryBtn} onClick={save} disabled={saving}>
          {i18n.t("flowResults.save")}
        </button>
      </div>
    </Dialog>
  );
};

export default MeasurementDialog;
